/**
 * AB#426: Markdown + YAML front-matter (de)serialization for local disk save/open.
 *
 * The field set is fixed and shared with the desktop Task's fs schema:
 *   site_slug, content_type, id, title, slug, status, excerpt, tags,
 *   seo_title, meta_description, featured_image
 *
 * Values are emitted as JSON scalars (double-quoted strings, `[...]` for tags).
 * That is valid, git/Claude-readable YAML AND round-trips exactly — including any
 * embedded quotes/newlines, which JSON escaping keeps on one physical line.
 *
 * ponytail: controlled subset (our own files), not a general YAML parser. Swap in a
 * real YAML lib only if we ever need to ingest arbitrary external front-matter.
 */

export interface PostFrontMatter {
  site_slug: string;
  content_type: string;
  id: string;
  title: string;
  slug: string;
  status: string;
  excerpt: string;
  tags: string[];
  seo_title: string;
  meta_description: string;
  featured_image: string;
}

/** Field order is fixed so output is stable/diff-friendly. */
const STRING_KEYS = [
  'site_slug',
  'content_type',
  'id',
  'title',
  'slug',
  'status',
  'excerpt',
  'seo_title',
  'meta_description',
  'featured_image',
] as const;

/** Serialize front-matter + body into a `.md` string. */
export function serializePost(fm: PostFrontMatter, body: string): string {
  const lines: string[] = ['---'];
  for (const key of STRING_KEYS) {
    lines.push(`${key}: ${JSON.stringify(fm[key] ?? '')}`);
  }
  lines.push(`tags: ${JSON.stringify(fm.tags ?? [])}`);
  lines.push('---');
  return lines.join('\n') + '\n' + body;
}

/** Parse a `.md` string back into front-matter + body. Missing keys default empty. */
export function parsePost(md: string): { frontMatter: PostFrontMatter; body: string } {
  const fm: PostFrontMatter = {
    site_slug: '',
    content_type: '',
    id: '',
    title: '',
    slug: '',
    status: '',
    excerpt: '',
    tags: [],
    seo_title: '',
    meta_description: '',
    featured_image: '',
  };

  const opening = md.match(/^---\r?\n/);
  if (!opening) return { frontMatter: fm, body: md };
  const remainder = md.slice(opening[0].length);
  const closing = /^---\r?$/m.exec(remainder);
  if (!closing) return { frontMatter: fm, body: md };
  const block = remainder.slice(0, closing.index);
  const bodyStart = closing.index + closing[0].length;
  const body = remainder.slice(bodyStart + (remainder[bodyStart] === '\n' ? 1 : 0));

  for (const line of block.split(/\r?\n/)) {
    const sep = line.indexOf(': ');
    if (sep === -1) continue;
    const key = line.slice(0, sep);
    const rawValue = line.slice(sep + 2);
    if (key === 'tags') {
      try {
        const parsed = JSON.parse(rawValue);
        if (Array.isArray(parsed)) fm.tags = parsed.map(String);
      } catch {
        /* leave default [] */
      }
    } else if ((STRING_KEYS as readonly string[]).includes(key)) {
      try {
        fm[key as (typeof STRING_KEYS)[number]] = String(JSON.parse(rawValue));
      } catch {
        fm[key as (typeof STRING_KEYS)[number]] = rawValue;
      }
    }
  }

  return { frontMatter: fm, body };
}
